import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import { toast } from 'react-toastify';
import moment from 'moment';

import api from '../../../services';

const useStyles = makeStyles((theme) => ({
  panel: { maxWidth: 640, paddingTop: theme.spacing(1) },
  item: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const ACTION_LABELS = {
  profile_updated: 'Profile updated',
  profile_photo_updated: 'Profile photo updated',
  payment_method_added: 'Payment method added',
  payment_method_removed: 'Payment method removed',
  payment_method_default: 'Default payment method',
  subscription_plan_changed: 'Subscription plan changed',
  subscription_seats_changed: 'Seats changed',
  session_revoked: 'Session revoked',
  sessions_revoked_others: 'Other sessions signed out',
};

export default function ActivityTab() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const load = async (nextSkip = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/settings/activity', { params: { limit, skip: nextSkip } });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
      setSkip(nextSkip);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
  }, []);

  return (
    <div className={classes.panel}>
      <Typography variant="h6" gutterBottom>
        Activity
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Recent changes from Settings, billing, subscription, and security.
      </Typography>
      {loading ? (
        <CircularProgress size={28} />
      ) : items.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No activity yet.
        </Typography>
      ) : (
        <>
          {items.map((row) => (
            <div key={row.id} className={classes.item}>
              <Typography variant="subtitle2">
                {ACTION_LABELS[row.action] || row.summary || row.action}
              </Typography>
              {row.summary && ACTION_LABELS[row.action] ? (
                <Typography variant="body2" color="textSecondary">
                  {row.summary}
                </Typography>
              ) : null}
              <Typography variant="caption" color="textSecondary">
                {row.actor?.email || row.actor?.username || 'User'} ·{' '}
                {row.createdAt ? moment(row.createdAt).format('MMM D, YYYY h:mm a') : ''}
              </Typography>
            </div>
          ))}
          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Button disabled={skip <= 0} onClick={() => load(Math.max(0, skip - limit))}>
              Previous
            </Button>
            <Typography variant="caption" color="textSecondary">
              {skip + 1}–{Math.min(skip + items.length, total)} of {total}
            </Typography>
            <Button disabled={skip + items.length >= total} onClick={() => load(skip + limit)}>
              Next
            </Button>
          </Box>
        </>
      )}
    </div>
  );
}
